import {
  useDownloadTrialExport,
  useTrialExport,
} from "@/api/hooks/use-trial-export";
import { formatTimestamp } from "@/lib/format";

interface TrialExportCardProps {
  tenantId: string;
}

/** Post-trial "your eval bundle is ready" card. Renders nothing until the
 * trial_export job has materialized a bundle for this tenant, so it's safe to
 * mount unconditionally on the converted-state billing view. */
export function TrialExportCard({ tenantId }: TrialExportCardProps) {
  const exportQ = useTrialExport(tenantId);
  const downloadM = useDownloadTrialExport(tenantId);
  const info = exportQ.data;
  if (!info || info.status !== "ready") return null;

  const sizeMb = ((info.size_bytes ?? 0) / 1_000_000).toFixed(1);
  return (
    <div
      data-testid="trial-export-card"
      className="rounded-lg border border-line-subtle bg-surface p-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-electric-blue">
            Trial results
          </p>
          <p className="text-sm text-fg">
            Your trial eval bundle is ready:{" "}
            <span className="font-mono">{info.pair_count}</span>{" "}
            baseline/optimized response pairs (
            <span className="font-mono">{sizeMb} MB</span> zip), formatted for
            an LLM judge.
          </p>
          <p data-testid="trial-export-expiry" className="text-xs text-fg-muted">
            {info.expires_at
              ? `Available until ${formatTimestamp(info.expires_at)} — ${
                  info.retention_days_after_first_download
                } days after your first download.`
              : `Kept until ${info.retention_days_after_first_download} days after your first download.`}
          </p>
        </div>
        <button
          type="button"
          data-testid="trial-export-download"
          onClick={() => downloadM.mutate()}
          disabled={downloadM.isPending}
          className="shrink-0 rounded-md bg-electric-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-electric-blue/80 disabled:opacity-50 transition-colors"
        >
          {downloadM.isPending ? "Downloading…" : "Download eval bundle"}
        </button>
      </div>
      {downloadM.isError && (
        <p className="mt-2 text-xs text-red-400" role="alert">
          Download failed — try again.
        </p>
      )}
    </div>
  );
}
